import { Input } from 'components/Forms'
import { useEffect, useState } from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import styled, { useTheme } from 'styled-components'
interface IProps {
  oldPassword?: string
  newPassword: string
  setNewPassword: (value: string) => void
  setIsValidForNextStep: (isValid: boolean) => void
  showExtraRequirement: boolean
}

export const PasswordSetup = ({
  oldPassword,
  newPassword,
  setNewPassword,
  setIsValidForNextStep,
  showExtraRequirement,
}: IProps) => {
  const validPasswordLength = 14
  const specialCharRegex = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/
  const lowerCaseCharRegex = /[a-z]/
  const upperCaseCharRegex = /[A-Z]/
  const numberCharRegex = /\d/
  const theme = useTheme()

  // password requirements state
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [isMatch, setIsMatch] = useState(false)
  const [isPasswordDifferentFromOld, setIsPasswordDifferentFromOld] = useState(false)
  const [isLong, setIsLong] = useState(false)
  const [hasSpecialChar, setHasSpecialChar] = useState(false)
  const [hasLowerCaseChar, setHasLowerCaseChar] = useState(false)
  const [hasUpperCaseChar, setHasUpperCaseChar] = useState(false)
  const [hasNumber, setHasNumber] = useState(false)

  useEffect(() => {
    setIsMatch(newPassword.trim().length > 0 && newPassword === confirmNewPassword)
    setIsPasswordDifferentFromOld(showExtraRequirement ? newPassword !== oldPassword : true)
    setIsLong(newPassword.trim().length >= validPasswordLength)
    setHasSpecialChar(specialCharRegex.test(newPassword))
    setHasLowerCaseChar(lowerCaseCharRegex.test(newPassword))
    setHasUpperCaseChar(upperCaseCharRegex.test(newPassword))
    setHasNumber(numberCharRegex.test(newPassword))
  }, [newPassword, oldPassword, showExtraRequirement, confirmNewPassword])
  useEffect(() => {
    setIsValidForNextStep(
      isMatch &&
        isPasswordDifferentFromOld &&
        isLong &&
        hasSpecialChar &&
        hasLowerCaseChar &&
        hasUpperCaseChar &&
        hasNumber,
    )
  }, [isMatch, isPasswordDifferentFromOld, isLong, hasSpecialChar, hasLowerCaseChar, hasUpperCaseChar, hasNumber])
  return (
    <Section>
      <section className="inputs-container">
        <Input
          name="new-password"
          label="New Password"
          placeholder="**********"
          value={newPassword}
          type="password"
          onChange={(e) => setNewPassword(e.target.value)}
          isPasswordWithReveal={true}
          autoComplete="new-password"
          data-testid="new-password-input"
        />
        <Input
          name="confirm-new-password"
          label="Confirm New Password"
          placeholder="**********"
          value={confirmNewPassword}
          type="password"
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          isPasswordWithReveal={true}
          autoComplete="new-password"
          data-testid="confirm-new-password-input"
        />
      </section>
      <section className="password-requirements-container">
        <p>Password requirements:</p>
        <section className="requirements">
          <ul>
            <li>
              <FaCheckCircle color={isMatch ? theme.colors.success.darker : theme.colors.primary.lightest} />
              Passwords match
            </li>
            <li>
              <FaCheckCircle color={isLong ? theme.colors.success.darker : theme.colors.primary.lightest} />
              Meets {validPasswordLength} character minimum
            </li>
            <li>
              <FaCheckCircle color={hasSpecialChar ? theme.colors.success.darker : theme.colors.primary.lightest} />
              Contains special character
            </li>
          </ul>
          <ul>
            <li>
              <FaCheckCircle color={hasLowerCaseChar ? theme.colors.success.darker : theme.colors.primary.lightest} />
              Contains lower case letter
            </li>
            <li>
              <FaCheckCircle color={hasUpperCaseChar ? theme.colors.success.darker : theme.colors.primary.lightest} />
              Contains upper case letter
            </li>
            <li>
              <FaCheckCircle color={hasNumber ? theme.colors.success.darker : theme.colors.primary.lightest} />
              Contains number
            </li>
          </ul>
        </section>
        {showExtraRequirement && (
          <section className="extra-requirements" data-testid="extra-requirements-container">
            <ul>
              <li>
                <FaCheckCircle
                  color={isPasswordDifferentFromOld ? theme.colors.success.darker : theme.colors.primary.lightest}
                />
                Does not match previous password
              </li>
            </ul>
          </section>
        )}
      </section>
    </Section>
  )
}

const Section = styled.section`
  .inputs-container {
    color: ${({ theme }) => theme.colors.grey.darker};
    ${({ theme }) => theme.typography.xxs.normal};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    margin-bottom: 1.25rem;
    & > div:first-child {
      margin-bottom: 1.25rem;
    }
    label > div {
      color: ${({ theme }) => theme.colors.grey.darker};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    }
  }

  .password-requirements-container {
    font-size: ${({ theme }) => theme.util.pixelToRem(11)};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    margin-bottom: 3rem;
    & p {
      color: ${({ theme }) => theme.colors.grey.darker};
      ${({ theme }) => theme.typography.xxs.normal};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    }
    .requirements,
    .extra-requirements {
      display: flex;
      justify-content: space-between;
      ul li {
        color: ${({ theme }) => theme.colors.grey.darker};
        svg {
          margin-right: 0.25rem;
          color: ${({ theme }) => theme.colors.primary.lightest};
        }
      }
    }
  }
`
